import { useState } from 'react';
import { useConfirm } from '@/components/DialogProvider';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteApartment } from '@/api/generated/sdk.gen';
import { apartmentQuery, type Apartment } from '@/lib/apartments';
import { ApiError } from '@/lib/api';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { ApartmentDetail, TAB_PERMISSIONS, type ApartmentTab } from '@/components/apartment/ApartmentDetail';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Derived from TAB_PERMISSIONS (the single source of truth for the
// ApartmentTab union) rather than a hand-maintained literal list — this
// route statically imports ApartmentDetail already (createFileRoute, not
// createLazyFileRoute), so the module is available at validateSearch time.
// It only needs to know which strings are well-formed tab values; permission-
// based fallback (forbidden but well-formed tab) happens inside ApartmentDetail.
const TAB_VALUES = Object.keys(TAB_PERMISSIONS) as ApartmentTab[];

export const Route = createFileRoute('/_app/apartments/$id')({
  // Catch 401/404 in the loader so the component owns the error UI — without
  // this ensureQueryData would throw past the component to a router error
  // boundary that isn't configured for this route. Same pattern as
  // bookings.$id.tsx.
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(apartmentQuery(params.id));
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 401 || status === 404) return;
      throw err;
    }
  },
  // Unknown ?tab= values are dropped here; forbidden-but-well-formed ones
  // fall back inside ApartmentDetail (it knows the operator's permissions).
  validateSearch: (search: Record<string, unknown>): { tab?: ApartmentTab } => {
    const t = search.tab;
    return typeof t === 'string' && (TAB_VALUES as readonly string[]).includes(t)
      ? { tab: t as ApartmentTab }
      : {};
  },
  component: ApartmentDetailPage,
});

function ApartmentDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, error, isLoading } = useQuery(apartmentQuery(id));

  // Title lives in the Topbar (navTitle → "Apartments"); publish the unit title
  // as the subtitle. Falls back to navSubtitle until the apartment loads.
  usePageHeader({ subtitle: data?.title });

  // replace:true — tab switching is not a browser-history event.
  const onTabChange = (t: ApartmentTab) =>
    navigate({ search: (prev) => ({ ...prev, tab: t }), replace: true });

  const is401 = error instanceof ApiError && error.status === 401;
  const is404 = error instanceof ApiError && error.status === 404;
  const errorMessage = is401
    ? 'Your session has expired.'
    : is404
      ? 'Apartment not found.'
      : error instanceof ApiError
        ? error.message
        : error
          ? String(error)
          : null;

  return (
    <div className="max-w-5xl">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted">
        <Link to="/apartments" className="hover:underline">Apartments</Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">{data?.code ?? id.slice(0, 8)}</span>
      </nav>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {errorMessage && (
        <div
          role="alert"
          className="mb-6 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
          {is401 && (
            <>
              {' '}
              <Link to="/login" className="font-medium underline">
                Sign in again
              </Link>
              .
            </>
          )}
        </div>
      )}

      {data && <ApartmentDetailBody apt={data} tab={tab ?? 'overview'} onTabChange={onTabChange} />}
    </div>
  );
}

function ApartmentDetailBody({
  apt, tab, onTabChange,
}: { apt: Apartment; tab: ApartmentTab; onTabChange: (t: ApartmentTab) => void }) {
  return (
    <>
      <header className="mb-6 flex items-start justify-between gap-4">
        <p className="flex items-center gap-2 text-sm text-muted">
          <span className="font-mono">{apt.code}</span>
          {apt.nickname && <span>· {apt.nickname}</span>}
          {!apt.is_active && <Badge variant="neutral">Inactive</Badge>}
        </p>
        {apt.is_active && <DeleteButton apt={apt} />}
      </header>

      <ApartmentDetail apt={apt} tab={tab} onTabChange={onTabChange} />
    </>
  );
}

function DeleteButton({ apt }: { apt: Apartment }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [err, setErr] = useState<string | null>(null);
  const askConfirm = useConfirm();

  const mut = useMutation({
    mutationFn: () =>
      deleteApartment({
        throwOnError: true,
        path: { id: apt.id },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartments'] });
      qc.invalidateQueries({ queryKey: ['apartments', apt.id] });
      toast({ kind: 'success', title: 'Apartment deleted', message: apt.title });
      void navigate({ to: '/apartments' });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="destructive"
        size="sm"
        loading={mut.isPending}
        disabled={mut.isPending}
        onClick={async () => {
          // Soft-delete only; Channex-side deactivation is a Phase-1
          // limitation (ADR-030 amendment) — operator must disconnect
          // OTA channels in Channex dashboard manually.
          const ok = await askConfirm({
            title: `Delete apartment "${apt.title}"?`,
            body:
              'Local soft-delete only. The matching listing stays active ' +
              'with the channel manager — disconnect its OTA channels ' +
              'there manually.',
            destructive: true,
            confirmLabel: 'Delete',
          });
          if (ok) {
            setErr(null);
            mut.mutate();
          }
        }}
      >
        Delete
      </Button>
      {err && (
        <p role="alert" className="text-xs text-destructive">
          {err}
        </p>
      )}
    </div>
  );
}
